import React from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  Heading,
  Input,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import dayjs from 'dayjs'
import { Link, useNavigate } from 'react-router-dom'

import xhr from 'xhr'
import useAuth from 'hooks/useAuth'
import getCountries from 'api/index/getCountries'
import { BRAND, PageHeader, SectionCard } from '../components/ui'
import {
  AD_SOURCES,
  CONTACT_ROLES,
  ENTRY_CHANNELS,
  HOW_FOUND,
  OBSERVATION_HELP,
  OBSERVATION_PLACEHOLDER,
  PROGRAM_PENDING,
  statusInfo,
} from './constants'
import useProgramTypes from './useProgramTypes'

const today = () => dayjs().format('YYYY-MM-DD')
const oneYearAgo = () => dayjs().subtract(1, 'year').format('YYYY-MM-DD')

const schema = Yup.object().shape({
  contact_date: Yup.string()
    .required('Elige la fecha en que te escribió.')
    .test('not-future', 'La fecha no puede ser futura.', (value) => !value || value <= today())
    .test('not-old', 'La fecha no puede tener más de un año.', (value) => !value || value >= oneYearAgo()),
  name: Yup.string().trim().required('Escribe el nombre del contacto.'),
  cellphone: Yup.string()
    .trim()
    .required('Escribe el celular.')
    .test('digits', 'El celular debe tener al menos 7 dígitos.', (value) => (value || '').replace(/\D/g, '').length >= 7),
  email: Yup.string().trim().email('El correo no es válido.'),
  entry_channel: Yup.string().required('Selecciona el canal de ingreso.'),
  observation: Yup.string().trim().required('Escribe qué motivó al contacto a buscar el programa.'),
  utm_content: Yup.string()
    .trim()
    .max(64, 'Máximo 64 caracteres.')
    .matches(/^[A-Za-z0-9_\s-]*$/, 'Solo letras, números, guiones y guiones bajos.'),
  how_found_detail: Yup.string()
    .trim()
    .max(120, 'Máximo 120 caracteres.')
    .when('how_found', {
      is: 'otro',
      then: (field) => field.required('Escribe cómo conoció Klazia.'),
    }),
  student_age: Yup.number()
    .transform((value, original) => (original === '' ? undefined : value))
    .typeError('Escribe solo números.')
    .min(3, 'Edad no válida.')
    .max(99, 'Edad no válida.'),
})

function SectionTitle({ step, children }) {
  return (
    <Flex align="center" gap={2} mb={4}>
      <Flex
        w="22px"
        h="22px"
        borderRadius="full"
        bg="purple.50"
        color={BRAND}
        fontSize="xs"
        fontWeight="700"
        align="center"
        justify="center"
      >
        {step}
      </Flex>
      <Heading as="h2" fontSize="md" fontWeight="600" color="gray.800">
        {children}
      </Heading>
    </Flex>
  )
}

function NuevoProspecto() {
  const { user } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()

  const programTypes = useProgramTypes()
  const [countries, setCountries] = React.useState([])
  const [duplicate, setDuplicate] = React.useState(null)

  const formik = useFormik({
    initialValues: {
      name: '',
      cellphone: '',
      country: '',
      email: '',
      contact_role: '',
      program: PROGRAM_PENDING,
      student_age: '',
      last_grade: '',
      contact_date: today(),
      entry_channel: 'whatsapp',
      how_found: 'pendiente',
      how_found_detail: '',
      observation: '',
      utm_source: '',
      utm_content: '',
    },
    validationSchema: schema,
    onSubmit: async (values, helpers) => {
      setDuplicate(null)
      try {
        // Sin fuente no se envía el anuncio (el campo queda deshabilitado).
        const res = await xhr.post('/prospects/manual', {
          ...values,
          utm_content: values.utm_source ? values.utm_content : '',
        })
        const created = res.data?.data || res.data
        toast({ title: 'Prospecto registrado.', status: 'success', duration: 3000, isClosable: true })
        navigate(`/plataforma/asesoras/ver-prospecto/${created.id}`)
      } catch (error) {
        const response = error?.response
        const apiError = response?.data?.error
        if (response?.status === 409 && apiError?.details) {
          setDuplicate(apiError.details)
          window.scrollTo({ top: 0, behavior: 'smooth' })
        } else {
          toast({
            title: 'No se pudo registrar el prospecto.',
            description: apiError?.message || 'Revisa los datos e inténtalo de nuevo.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          })
        }
      } finally {
        helpers.setSubmitting(false)
      }
    },
  })

  React.useEffect(() => {
    const load = async () => {
      try {
        const list = await getCountries()
        setCountries(list)
        const colombia = list.find((el) => el.code === 'CO' || String(el.phone_code) === '57')
        if (colombia) formik.setFieldValue('country', String(colombia.id))
      } catch (error) {
        console.error(error)
      }
    }
    load()
  }, [])

  const fieldProps = (name) => ({
    name,
    value: formik.values[name],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
  })
  const invalid = (name) => Boolean(formik.touched[name] && formik.errors[name])

  return (
    <Box maxW="960px">
      <PageHeader
        breadcrumb={[{ label: 'Escritorio', to: '/plataforma/asesoras' }, { label: 'Agregar prospecto' }]}
        title="Agregar prospecto"
        subtitle="Registra a quien te contactó por WhatsApp, llamada u otro canal."
      />

      {duplicate && (
        <Alert status="warning" variant="left-accent" borderRadius="md" mb={5} alignItems="flex-start">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="sm">Este contacto ya está registrado</AlertTitle>
            <AlertDescription fontSize="sm">
              {duplicate.adviser?.isMe ? (
                <>
                  Ya lo tienes en tu Escritorio como prospecto #{duplicate.prospect.id} (
                  {statusInfo(duplicate.prospect.status).label}).{' '}
                  <Text as={Link} to={`/plataforma/asesoras/ver-prospecto/${duplicate.prospect.id}`} color={BRAND} fontWeight="600">
                    Ver prospecto
                  </Text>
                </>
              ) : (
                <>
                  El prospecto #{duplicate.prospect.id} ({duplicate.prospect.name}) coincide por{' '}
                  {duplicate.prospect.matchedBy === 'email' ? 'correo' : 'celular'} y está asignado a{' '}
                  <b>{duplicate.adviser?.name || 'otra asesora'}</b> desde el{' '}
                  {dayjs(duplicate.prospect.createdAt).format('DD/MM/YYYY')}. La venta es de quien hizo el primer
                  contacto. Si necesitas tomarlo, pide la transferencia.
                </>
              )}
            </AlertDescription>
          </Box>
        </Alert>
      )}

      <form onSubmit={formik.handleSubmit} noValidate>
        <SectionCard padding={{ base: 4, md: 6 }}>
          <SectionTitle step={1}>Datos del contacto</SectionTitle>
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
            <FormControl isRequired isInvalid={invalid('name')} gridColumn={{ md: 'span 2' }}>
              <FormLabel fontSize="sm">Nombre</FormLabel>
              <Input id="name" placeholder="Nombre del interesado" {...fieldProps('name')} />
              <FormErrorMessage>{formik.errors.name}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={invalid('cellphone')}>
              <FormLabel fontSize="sm">Celular</FormLabel>
              <Input id="cellphone" type="tel" placeholder="Número de contacto" {...fieldProps('cellphone')} />
              <FormErrorMessage>{formik.errors.cellphone}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">País y prefijo</FormLabel>
              <Select id="country" {...fieldProps('country')}>
                <option value="">Sin país</option>
                {countries.map((el) => (
                  <option key={el.id} value={el.id}>
                    {el.name} {el.phone_code ? `+${el.phone_code}` : ''}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl isInvalid={invalid('email')}>
              <FormLabel fontSize="sm">Correo</FormLabel>
              <Input id="email" type="email" placeholder="Opcional" {...fieldProps('email')} />
              <FormErrorMessage>{formik.errors.email}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Programa de interés</FormLabel>
              <Select id="program" {...fieldProps('program')}>
                <option value={PROGRAM_PENDING}>Por definir</option>
                {programTypes.map((el) => (
                  <option key={el.id} value={el.slug}>
                    {el.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl gridColumn={{ md: 'span 2' }}>
              <FormLabel fontSize="sm">¿Quién escribe?</FormLabel>
              <RadioGroup
                id="contact_role"
                value={formik.values.contact_role}
                onChange={(value) => formik.setFieldValue('contact_role', value)}
              >
                <Stack direction={{ base: 'column', sm: 'row' }} spacing={6}>
                  {CONTACT_ROLES.map((el) => (
                    <Radio key={el.value} value={el.value} colorScheme="purple">
                      {el.label}
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            </FormControl>

            <FormControl isInvalid={invalid('student_age')}>
              <FormLabel fontSize="sm">Edad del estudiante</FormLabel>
              <Input id="student_age" type="number" placeholder="Opcional" {...fieldProps('student_age')} />
              <FormErrorMessage>{formik.errors.student_age}</FormErrorMessage>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Último grado aprobado</FormLabel>
              <Input id="last_grade" placeholder="Opcional. Ej.: 9°" {...fieldProps('last_grade')} />
            </FormControl>
          </Grid>

          <Divider my={6} />

          <SectionTitle step={2}>Origen</SectionTitle>
          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
            <FormControl isRequired isInvalid={invalid('contact_date')}>
              <FormLabel fontSize="sm">Fecha de contacto</FormLabel>
              <Input id="contact_date" type="date" max={today()} min={oneYearAgo()} {...fieldProps('contact_date')} />
              {!invalid('contact_date') && (
                <FormHelperText fontSize="xs">Día en que el contacto te escribió, por ejemplo en HiBot.</FormHelperText>
              )}
              <FormErrorMessage>{formik.errors.contact_date}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={invalid('entry_channel')}>
              <FormLabel fontSize="sm">Canal de ingreso</FormLabel>
              <Select id="entry_channel" {...fieldProps('entry_channel')}>
                {ENTRY_CHANNELS.map((el) => (
                  <option key={el.value} value={el.value}>
                    {el.label}
                  </option>
                ))}
              </Select>
              <FormHelperText fontSize="xs">Por dónde llegó el contacto.</FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Cómo conoció Klazia</FormLabel>
              <Select id="how_found" {...fieldProps('how_found')}>
                {HOW_FOUND.map((el) => (
                  <option key={el.value} value={el.value}>
                    {el.label}
                  </option>
                ))}
              </Select>
              <FormHelperText fontSize="xs">Lo que responde la persona. Puedes completarlo después.</FormHelperText>
            </FormControl>

            {formik.values.how_found === 'otro' && (
              <FormControl isRequired isInvalid={invalid('how_found_detail')} gridColumn={{ md: '2' }}>
                <FormLabel fontSize="sm">¿Cuál?</FormLabel>
                <Input
                  id="how_found_detail"
                  placeholder="Ej.: emisora local, feria educativa, YouTube"
                  maxLength={120}
                  {...fieldProps('how_found_detail')}
                />
                <FormErrorMessage>{formik.errors.how_found_detail}</FormErrorMessage>
              </FormControl>
            )}

            {/* Campaña en su propia fila para que Fuente y Anuncio queden juntos. */}
            <Grid gridColumn={{ md: 'span 2' }} templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
              <FormControl>
                <FormLabel fontSize="sm">Fuente</FormLabel>
                <Select id="utm_source" placeholder="No vino de un anuncio" {...fieldProps('utm_source')}>
                  {AD_SOURCES.map((el) => (
                    <option key={el.value} value={el.value}>
                      {el.label}
                    </option>
                  ))}
                </Select>
                <FormHelperText fontSize="xs">Si escribió desde un anuncio, por ejemplo de Facebook a WhatsApp.</FormHelperText>
              </FormControl>

              <FormControl isInvalid={invalid('utm_content')}>
                <FormLabel fontSize="sm">Anuncio</FormLabel>
                <Input
                  id="utm_content"
                  placeholder="Ej.: 120212345678900123"
                  maxLength={64}
                  isDisabled={!formik.values.utm_source}
                  {...fieldProps('utm_content')}
                />
                {!invalid('utm_content') && (
                  <FormHelperText fontSize="xs">ID del anuncio: cópialo de HiBot o del anuncio. Opcional.</FormHelperText>
                )}
                <FormErrorMessage>{formik.errors.utm_content}</FormErrorMessage>
              </FormControl>
            </Grid>

            <FormControl isRequired isInvalid={invalid('observation')} gridColumn={{ md: 'span 2' }}>
              <FormLabel fontSize="sm">Observación</FormLabel>
              <Textarea
                id="observation"
                rows={3}
                placeholder={OBSERVATION_PLACEHOLDER}
                {...fieldProps('observation')}
              />
              {!invalid('observation') && <FormHelperText fontSize="xs">{OBSERVATION_HELP}</FormHelperText>}
              <FormErrorMessage>{formik.errors.observation}</FormErrorMessage>
            </FormControl>
          </Grid>

          <Alert status="info" bg="blue.50" borderRadius="md" mt={8} py={3} alignItems="flex-start">
            <AlertIcon boxSize={4} mt="2px" />
            <AlertDescription fontSize="sm" color="gray.700">
              El prospecto queda a cargo de {user.first_name || 'ti'}. La venta se atribuye a la asesora que hizo el
              primer contacto.
            </AlertDescription>
          </Alert>

          <Divider my={5} />

          <Flex justify="space-between" gap={3}>
            <Button as={Link} to="/plataforma/asesoras" variant="outline">
              Cancelar
            </Button>
            <Button
              type="submit"
              bg={BRAND}
              color="white"
              _hover={{ bg: '#463fd1' }}
              isLoading={formik.isSubmitting}
              loadingText="Guardando"
            >
              Guardar prospecto
            </Button>
          </Flex>
        </SectionCard>
      </form>
    </Box>
  )
}

export default NuevoProspecto
